<template>
	<b-container>
		<Enter2FA
			ref="enter2FA"
			:callback="codeEntered"
			:is-setup="secondFactorSetupRequired"
			:invite="!inviteVerified"
			@resend="phoneEntered"
			@setup="reenterPhoneNumber"
		/>
		<Setup2FA ref="setup2FA" :callback="phoneEntered" />

		<b-card v-if="inviteVerified || !isInvite" class="mt-5 mx-auto bg-none border-none">
			<validation-observer v-slot="{ handleSubmit }" ref="validationObserver">
				<form @submit.prevent="handleSubmit(onSubmit)">
					<span v-if="step === 1">
						<b-row>
							<b-col><h1>{{ $t("signup.userinfo_headline") }}</h1></b-col>
						</b-row>
						<b-row>
							<b-col>
								<validation-provider v-slot="{ errors }" rules="required">
									<b-form-group :label="$t('signup.pronoun')">
										<b-form-radio-group
											v-model="enterpriseInfo.owner.pronoun"
										>
											<b-form-radio value="mr">
												Mr
											</b-form-radio>
											<b-form-radio value="ms">
												Ms
											</b-form-radio>
											<b-form-radio value="mx">
												Mx
											</b-form-radio>
										</b-form-radio-group>
										<small class="text-white d-block bg-secondary px-1">{{ errors[0] }}</small>
									</b-form-group>
								</validation-provider>
							</b-col>
						</b-row>
						<b-row>
							<b-col>
								<validation-provider v-slot="{ errors }" rules="required">
									<b-form-group :label="$t('signup.firstname')">
										<b-form-input v-model="enterpriseInfo.owner.firstName" />
										<small class="text-white d-block bg-secondary px-1">{{ errors[0] }}</small>
									</b-form-group>
								</validation-provider>
							</b-col>
							<b-col>
								<validation-provider v-slot="{ errors }" rules="required">
									<b-form-group :label="$t('signup.lastname')">
										<b-form-input v-model="enterpriseInfo.owner.lastName" />
										<small class="text-white d-block bg-secondary px-1">{{ errors[0] }}</small>
									</b-form-group>
								</validation-provider>
							</b-col>
						</b-row>
						<b-row>
							<b-col>
								<validation-provider v-slot="{ errors }" rules="required|email">
									<b-form-group :label="$t('signup.email')">
										<b-form-input v-model="enterpriseInfo.owner.email" />
										<small class="text-white d-block bg-secondary px-1">{{ errors[0] }}</small>
									</b-form-group>
								</validation-provider>
							</b-col>
							<b-col>
								<validation-provider v-slot="{ errors }" rules="required">
									<b-form-group :label="$t('signup.language')">
										<b-form-select v-model="enterpriseInfo.owner.language" :options="language_list" />
										<small class="text-white d-block bg-secondary px-1">{{ errors[0] }}</small>
									</b-form-group>
								</validation-provider>
							</b-col>
						</b-row>
						<b-row>
							<b-col>
								<validation-provider v-slot="{ errors }" rules="required|strong_password">
									<b-form-group :label="$t('signup.password')">
										<b-form-input v-model="enterpriseInfo.owner.password" type="password" />
										<small class="text-white d-block bg-secondary px-1">{{ errors[0] }}</small>
									</b-form-group>
								</validation-provider>
							</b-col>
							<b-col>
								<validation-provider v-slot="{ errors }" rules="required|confirm">
									<b-form-group :label="$t('signup.password_verify')">
										<b-form-input v-model="enterpriseInfo.owner.password_verify" type="password" />
										<small class="text-white d-block bg-secondary px-1">{{ errors[0] }}</small>
									</b-form-group>
								</validation-provider>
							</b-col>
						</b-row>
						<b-row>
							<b-col>
								<validation-provider v-slot="{ errors }" rules="required">
									<b-form-group :label="$t('signup.title')">
										<b-form-input v-model="enterpriseInfo.owner.jobTitle" />
										<small class="text-white d-block bg-secondary px-1">{{ errors[0] }}</small>
									</b-form-group>
								</validation-provider>
							</b-col>
						</b-row>
						<b-row>
							<b-col>
								<validation-provider v-slot="{ errors }" rules="required">
									<b-form-group :label="$t('signup.sector')">
										<b-form-select v-model="enterpriseInfo.owner.sector" :options="user_sector_list" @change="updateUserFocusOptions" />
										<small class="text-white d-block bg-secondary px-1">{{ errors[0] }}</small>
									</b-form-group>
								</validation-provider>
							</b-col>
							<b-col>
								<validation-provider v-slot="{ errors }" rules="required">
									<b-form-group :label="$t('signup.focus')">
										<b-form-select
											v-if="enterpriseInfo.owner.sector != 'other'"
											v-model="enterpriseInfo.owner.focus"
											:disabled="!enterpriseInfo.owner.sector"
											:options="user_focus_list"
										/>
										<b-form-input v-else v-model="enterpriseInfo.owner.focus" />
										<small class="text-white d-block bg-secondary px-1">{{ errors[0] }}</small>
									</b-form-group>
								</validation-provider>
							</b-col>
						</b-row>
						<b-row>
							<b-col>
								<validation-provider v-slot="{ errors }" rules="required_terms">
									<b-form-checkbox v-model="enterpriseInfo.owner.tos">
										{{ $t("signup.terms_and_conditions_prefix") }} <router-link target="_blank" to="/terms_of_service">
											{{ $t("signup.terms_and_conditions_link") }}
										</router-link>
									</b-form-checkbox>
									<small class="text-white d-block bg-secondary px-1">{{ errors[0] }}</small>
								</validation-provider>
							</b-col>
							<b-col>
								<b-button variant="dark" type="submit" block>
									{{ $t("signup.proceed_to_step_two") }}
								</b-button>
							</b-col>
						</b-row>
					</span>
					<span v-if="step === 2">
						<b-row>
							<b-col><h1>{{ $t("signup.enterpriseinfo_headline") }}</h1></b-col>
						</b-row>
						<b-row>
							<b-col>
								<validation-provider v-slot="{ errors }" rules="required">
									<b-form-group :label="$t('signup.name')">
										<b-form-input v-model="enterpriseInfo.name" />
										<small class="text-white d-block bg-secondary px-1">{{ errors[0] }}</small>
									</b-form-group>
								</validation-provider>
							</b-col>
							<b-col>
								<validation-provider v-slot="{ errors }" rules="required">
									<b-form-group :label="$t('signup.office_location')">
										<b-form-input v-model="enterpriseInfo.office" />
										<small class="text-white d-block bg-secondary px-1">{{ errors[0] }}</small>
									</b-form-group>
								</validation-provider>
							</b-col>
						</b-row>
						<b-row>
							<b-col cols="6">
								<validation-provider v-slot="{ errors }" rules="required">
									<b-form-group :label="$t('signup.sector')">
										<b-form-select v-model="enterpriseInfo.sector" :options="enterprise_sector_list" @change="updateEnterpriseFocusOptions" />
										<small class="text-white d-block bg-secondary px-1">{{ errors[0] }}</small>
									</b-form-group>
								</validation-provider>
							</b-col>
							<b-col cols="6">
								<validation-provider v-slot="{ errors }" rules="required">
									<b-form-group :label="$t('signup.focus')">
										<b-form-select
											v-if="enterpriseInfo.sector !== 'other'"
											v-model="enterpriseInfo.sector2"
											:disabled="!enterpriseInfo.sector"
											:options="enterprise_focus_list"
										/>
										<b-form-input v-else v-model="enterpriseInfo.sector2" />
										<small class="text-white d-block bg-secondary px-1">{{ errors[0] }}</small>
									</b-form-group>
								</validation-provider>
							</b-col>
						</b-row>
						<b-row>
							<b-col>
								<validation-provider v-slot="{ errors }" rules="required">
									<b-form-group :label="$t('signup.address_line') + ' 1'">
										<b-form-input v-model="enterpriseInfo.address" />
										<small class="text-white d-block bg-secondary px-1">{{ errors[0] }}</small>
									</b-form-group>
								</validation-provider>
							</b-col>
						</b-row>
						<b-row>
							<b-col>
								<validation-provider v-slot="{ errors }" rules="">
									<b-form-group :label="$t('signup.address_line') + ' 2'">
										<b-form-input v-model="enterpriseInfo.address2" />
										<small class="text-white d-block bg-secondary px-1">{{ errors[0] }}</small>
									</b-form-group>
								</validation-provider>
							</b-col>
						</b-row>
						<b-row>
							<b-col>
								<validation-provider v-slot="{ errors }" rules="required">
									<b-form-group :label="$t('signup.city')">
										<b-form-input v-model="enterpriseInfo.city" />
										<small class="text-white d-block bg-secondary px-1">{{ errors[0] }}</small>
									</b-form-group>
								</validation-provider>
							</b-col>
							<b-col>
								<validation-provider v-slot="{ errors }" rules="required">
									<b-form-group :label="$t('signup.postal_code')">
										<b-form-input v-model="enterpriseInfo.postalCode" />
										<small class="text-white d-block bg-secondary px-1">{{ errors[0] }}</small>
									</b-form-group>
								</validation-provider>
							</b-col>
						</b-row>
						<b-row>
							<b-col>
								<validation-provider v-slot="{ errors }" rules="required">
									<b-form-group :label="$t('signup.state')">
										<b-form-input v-model="enterpriseInfo.state" />
										<small class="text-white d-block bg-secondary px-1">{{ errors[0] }}</small>
									</b-form-group>
								</validation-provider>
							</b-col>
							<b-col>
								<validation-provider v-slot="{ errors }" rules="required">
									<b-form-group :label="$t('signup.country')">
										<b-form-select v-model="enterpriseInfo.country" :options="country_list" @change="setCallingCode" />
										<small class="text-white d-block bg-secondary px-1">{{ errors[0] }}</small>
									</b-form-group>
								</validation-provider>
							</b-col>
						</b-row>
						<b-row>
							<b-col>
								<validation-provider v-slot="{ errors }" rules="required">
									<b-form-group :label="$t('signup.phone')">
										<b-form-input v-model="enterpriseInfo.phone" type="tel" />
										<small class="text-white d-block bg-secondary px-1">{{ errors[0] }}</small>
									</b-form-group>
								</validation-provider>
							</b-col>
						</b-row>
						<b-row>
							<b-col>
								<b-button variant="secondary" block @click="step = 1">
									{{ $t("signup.back_to_step_one") }}
								</b-button>
							</b-col>
							<b-col>
								<b-button variant="primary" type="submit" block>
									{{ $t("signup.complete") }}
								</b-button>
							</b-col>
						</b-row>
					</span>
					<span v-if="step === 3">
						<h1>{{ $t("signup.success_headline") }}</h1>
					</span>
				</form>
			</validation-observer>
		</b-card>
		<span v-if="step === 4" />
	</b-container>
</template>

<script>
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate';
import { required, email } from 'vee-validate/dist/rules';
import Enter2FA from '@/views/user/Enter2FA.vue';
import Setup2FA from '@/views/user/Setup2FA.vue';

const countries = require('i18n-iso-countries');
countries.registerLocale(require('i18n-iso-countries/langs/en.json'));

const callingCodes = require('country-codes-list').customList('countryCode', '+{countryCallingCode} ');

export default {
	name: 'SignUp',
	components: {
		ValidationProvider,
		ValidationObserver,
		Enter2FA,
		Setup2FA,
	},
	data() {
		return {
			isInvite: false,
			inviteVerified: false,
			form: {
				email: '',
				password: '',
				phone: '',
			},
			step: 1,
			authToken: null,
			secondFactorSetupRequired: false,
			errortext: '',
			enterpriseInfo: {
				name: '',
				office: '',
				sector: '',
				sector2: '',
				address: '',
				address2: '',
				city: '',
				postalCode: '',
				state: '',
				country: 'us',
				phone: '',
				owner: {
					pronoun: '',
					firstName: '',
					lastName: '',
					email: '',
					language: 'en',
					password: '',
					password_verify: '',
					jobTitle: '',
					sector: '',
					focus: '',
				},
			},
			language_list: [{ value: 'en', text: 'English' }],
			country_list: [],
			user_sector_list: Object.entries(this.$t('signup.user_sector_focus_options')).map((value) => ({
				value: value[0],
				text: value[1].text,
			})),
			enterprise_sector_list: Object.entries(this.$t('signup.enterprise_sector_focus_options')).map((value) => ({
				value: value[0],
				text: value[1].text,
			})),
			user_focus_list: [],
			enterprise_focus_list: [],
		};
	},
	mounted() {
		// set up validation rules
		if (this.$route.params.confirmationCode) {
			this.isInvite = true;

			this.axios
				.post('/authenticate', {
					status: 'verify',
					code: this.$route.params.confirmationCode,
				})
				.then((res) => {
					this.$refs.enter2FA.show();
					this.authToken = res.data.authToken;
				})
				.catch(() => {
					this.$refs.enter2FA.hide();
					const globalModalOptions = {
						message: 'This is an invalid invitation link.',
						ok: true,
					};
					this.$msgBoxConfirm(globalModalOptions);
				});
		}
		extend('email', {
			...email,
			message: this.$t('signup.error.invalid_email'),
		});

		extend('required', {
			...required,
			message: this.$t('signup.error.field_required'),
		});

		extend('required_terms', {
			...required,
			message: this.$t('signup.error.accept_tos'),
		});

		extend('strong_password', {
			validate: (value) => value.length > 7,
			message: this.$t('signup.error.password_too_short'),
		});

		extend('confirm', {
			validate: (value) => value === this.enterpriseInfo.owner.password,
			message: this.$t('signup.error.passwords_dont_match'),
		});

		const countryMap = countries.getNames(this.$i18n.locale, { select: 'official' });

		this.country_list = Object.keys(countryMap).map((k) => ({ value: k.toLowerCase(), text: countryMap[k] }));
	},
	methods: {
		makeErrorToast(title, error) {
			this.$bvToast.toast(`${error}`, {
				title,
				autoHideDelay: 5000,
				appendToast: false,
				variant: 'danger',
			});
		},
		async onSubmit() {
			// form validation complete
			if (this.step === 1) {
				// if the user navigated to the signup component from the email confirmation link
				// validate that the user is invited
				if (this.isInvite) {
					const isConfirmed = await this.axios
						.post('/authenticate', {
							code: this.$route.params.confirmationCode,
						})
						.catch((err) => {
							this.makeErrorToast('Confirmation Error', err);
						});

					if (isConfirmed && isConfirmed.status === 200) {
						this.form.email = this.enterpriseInfo.owner.email;
						this.form.password = isConfirmed.data.password;

						this.axios
							.post('/signin', {
								email: this.form.email,
								password: this.form.password,
								userInfo: this.enterpriseInfo.owner,
							})
							.then((signinResponse) => {
								// since this is an invited signup, skip the enterprise form
								this.step = 3;
								this.errortext = '';
								if (signinResponse.data.secondFactorSetupRequired) {
									this.secondFactorSetupRequired = true;
									this.$refs.setup2FA.show();
								} else {
									this.$refs.enter2FA.show();
								}
							})
							.catch(() => {
								if (isConfirmed.data.email !== this.enterpriseInfo.owner.email) {
									this.makeErrorToast(
										'Confirmation Error',
										'The email address does not match an invited user. You must confirm your registration with the same email address used for the invite.'
									);
								}
								this.$refs.enter2FA.hide();
								this.errortext = this.$t('loginform.login-failed');
							});
					}
				} else {
					// the user went directly to /signup
					this.step = 2;
				}
				this.$refs.validationObserver.reset();
			} else if (this.step === 2) {
				// if enterprise signup send new user info request
				// TODO
				await this.axios
					.post('/signup', this.enterpriseInfo)
					.then(() => {
						this.errortext = '';
						this.step = 3;
					})
					.catch(() => {
						this.errortext = this.$t('loginform.login-failed');
						this.step = 4;
					});
			}
		},
		phoneEntered(phone) {
			if (phone) {
				this.form.phone = phone;
			}
			this.axios
				.post('/signin', {
					email: this.form.email,
					password: this.form.password,
					secondFactorPhone: this.form.phone,
				})
				.then((signinResponse) => {
					this.authToken = signinResponse.data.authToken;
					this.$refs.setup2FA.hide();
					this.$refs.enter2FA.show();
				})
				.catch(() => {
					this.$refs.setup2FA.hide();
					this.errortext = this.$t('loginform.login-failed');
				});
		},
		codeEntered(code) {
			this.axios
				.post('/authenticate', {
					authToken: this.authToken,
					secondFactor: code,
					invite: this.isInvite && !this.inviteVerified,
				})
				.then((authenticateResponse) => {
					if (authenticateResponse.data.user) {
						this.$store.commit('setUser', authenticateResponse.data.user);
						// add api token for all future requests
						this.$store.commit('setAuthToken', authenticateResponse.data.apiToken);
						this.$refs.enter2FA.hide();
						this.$router.replace('/');
					} else if (authenticateResponse.data.invite) {
						this.$refs.enter2FA.hide();
						this.inviteVerified = true;
					}
				})
				.catch(() => {
					this.$refs.enter2FA.show();
					this.$refs.enter2FA.errortext = this.$t('loginform.code-invalid');
					throw new Error();
				});
		},
		reenterPhoneNumber() {
			this.$refs.enter2FA.clearTimer();
			this.$refs.setup2FA.show();
			this.$refs.enter2FA.hide();
		},
		updateUserFocusOptions(userSectorValue) {
			this.user_focus_list = this.$t('signup.user_sector_focus_options')[userSectorValue].options;
		},
		updateEnterpriseFocusOptions(enterpriseSectorValue) {
			this.enterprise_focus_list = this.$t('signup.enterprise_sector_focus_options')[
				enterpriseSectorValue
			].options;
		},
		setCallingCode(countryCode) {
			if (!this.enterpriseInfo.phone) {
				this.enterpriseInfo.phone = callingCodes[countryCode.toUpperCase()];
			}
		},
	},
};
</script>

<style scoped>
.card {
	width: 42rem;
}
</style>
